// src/pages/inventory/rimender-stock/rimenderStockService.js
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Helper: ubah onValue menjadi Promise
export const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: konversi tanggal DD-MM-YYYY ke format yang bisa dibandingkan (YYYY-MM-DD)
export const convertDateForComparison = (dateStr) => {
    if (!dateStr) return "";
    // Format input: DD-MM-YYYY
    const parts = dateStr.split("-");
    if (parts.length === 3) {
        // Return YYYY-MM-DD untuk perbandingan
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
};

// Helper: mendapatkan nama bulan dari angka bulan
export const getMonthName = (monthNum) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months[parseInt(monthNum) - 1] || "";
};

// Helper: memformat header dengan bulan dan tahun dari tanggal
export const formatMonthYearHeader = (dateStr) => {
    if (!dateStr) return "Forecast";

    const parts = dateStr.split("-");
    if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2].slice(-2); // Ambil 2 digit terakhir tahun

        const monthName = getMonthName(month);
        return `${monthName}-${year}`;
    }

    return "Forecast";
};

// Fetch data Material Master
export const fetchMaterialMaster = async () => {
    try {
        const materialMasterRef = ref(database, "db_inventory/db_master_data/tb_material_master");
        const snapshot = await onValuePromise(materialMasterRef);
        const data = snapshot.val();

        const loadedData = [];

        if (data) {
            Object.keys(data).forEach((key) => {
                const record = data[key];
                loadedData.push({
                    firebaseKey: key,
                    index: record.index || "",
                    kode_material: record.kode_material || key,
                    id_material: record.id_material || "",
                    nama_produk: record.nama_produk || "",
                    id_satuan: record.id_satuan || "",
                    deskripsi: record.deskripsi || "",
                });
            });

            // Sort by index (ascending)
            loadedData.sort((a, b) => {
                const indexA = a.index ? parseInt(a.index) : 0;
                const indexB = b.index ? parseInt(b.index) : 0;
                return indexA - indexB;
            });
        }

        return loadedData;
    } catch (error) {
        console.error("Error fetching material master:", error);
        throw error;
    }
};

// Fetch data UOM
export const fetchUomData = async () => {
    try {
        const uomRef = ref(database, "db_inventory/db_master_data/tb_uom");
        const snapshot = await onValuePromise(uomRef);
        const data = snapshot.val();

        const loadedData = [];

        if (data) {
            Object.keys(data).forEach((key) => {
                const record = data[key];
                loadedData.push({
                    id_satuan: record.id_satuan || key,
                    nama_satuan: record.nama_satuan || "",
                });
            });
        }

        return loadedData;
    } catch (error) {
        console.error("Error fetching UOM data:", error);
        throw error;
    }
};

// Fetch data Material Category
export const fetchMaterialCategory = async () => {
    try {
        const categoryRef = ref(database, "db_inventory/db_master_data/tb_material_category");
        const snapshot = await onValuePromise(categoryRef);
        const data = snapshot.val();

        const loadedData = [];

        if (data) {
            Object.keys(data).forEach((key) => {
                const record = data[key];
                loadedData.push({
                    id_material: record.id_material || key,
                    nama_kategori_material: record.nama_kategori_material || "",
                });
            });
        }

        return loadedData;
    } catch (error) {
        console.error("Error fetching material category:", error);
        throw error;
    }
};

// Fetch data Forecast dari MonitoringStokBahanPG4
export const fetchForecastData = async () => {
    try {
        const forecastRef = ref(database, "db_inventory/db_stok_bahan");
        const snapshot = await onValuePromise(forecastRef);
        const data = snapshot.val();

        const forecastMap = {};

        if (data) {
            Object.keys(data).forEach((itemCode) => {
                const itemData = data[itemCode];

                // Akses data dari node forecast
                if (itemData.forecast) {
                    Object.keys(itemData.forecast).forEach((tanggalKey) => {
                        const node = itemData.forecast[tanggalKey];

                        if (!forecastMap[itemCode]) {
                            forecastMap[itemCode] = {};
                        }

                        // Gunakan tanggal_update dari node jika ada, atau gunakan tanggalKey
                        const tanggalUpdate = node.tanggal_update || tanggalKey;

                        forecastMap[itemCode][tanggalUpdate] = {
                            // Fresh Pineapple - Ambil semua M1-M6
                            fresh_pineapple_M1: node.forecast_fresh_pineapple?.M1 || "",
                            fresh_pineapple_M2: node.forecast_fresh_pineapple?.M2 || "",
                            fresh_pineapple_M3: node.forecast_fresh_pineapple?.M3 || "",
                            fresh_pineapple_M4: node.forecast_fresh_pineapple?.M4 || "",
                            fresh_pineapple_M5: node.forecast_fresh_pineapple?.M5 || "",
                            fresh_pineapple_M6: node.forecast_fresh_pineapple?.M6 || "",

                            // Banana - Ambil semua M1-M6
                            banana_M1: node.forecast_banana?.M1 || "",
                            banana_M2: node.forecast_banana?.M2 || "",
                            banana_M3: node.forecast_banana?.M3 || "",
                            banana_M4: node.forecast_banana?.M4 || "",
                            banana_M5: node.forecast_banana?.M5 || "",
                            banana_M6: node.forecast_banana?.M6 || "",

                            // Other Fresh Fruits - Ambil semua M1-M3
                            other_fresh_fruits_M1: node.forecast_other_fresh_fruits?.M1 || "",
                            other_fresh_fruits_M2: node.forecast_other_fresh_fruits?.M2 || "",
                            other_fresh_fruits_M3: node.forecast_other_fresh_fruits?.M3 || "",

                            // Simpan tanggal_update untuk referensi
                            tanggal_update: tanggalUpdate,
                        };
                    });
                }
            });
        }

        return forecastMap;
    } catch (error) {
        console.error("Error fetching forecast data:", error);
        throw error;
    }
};

// Fetch data Actual dari RealisasiStokBahanPG4
export const fetchActualData = async () => {
    try {
        const actualRef = ref(database, "db_inventory/db_stok_bahan");
        const snapshot = await onValuePromise(actualRef);
        const data = snapshot.val();

        const actualMap = {};

        if (data) {
            Object.keys(data).forEach((itemCode) => {
                const itemData = data[itemCode];

                // Akses data dari node realisasi
                if (itemData.realisasi) {
                    Object.keys(itemData.realisasi).forEach((tanggalKey) => {
                        const node = itemData.realisasi[tanggalKey];

                        if (!actualMap[itemCode]) {
                            actualMap[itemCode] = {};
                        }

                        // Pastikan node memiliki nilai
                        if (node) {
                            // Gunakan tanggal_update dari node jika ada, atau gunakan tanggalKey
                            const tanggalUpdate = node.tanggal_update || tanggalKey;

                            actualMap[itemCode][tanggalUpdate] = {
                                // Fresh Pineapple
                                fresh_pineapple: node.values?.fresh_pineapple || "",

                                // Banana
                                banana: node.values?.banana || "",

                                // Other Fresh Fruits
                                other_fresh_fruits: node.values?.other_fresh_fruits || "",

                                // Simpan tanggal_update untuk referensi
                                tanggal_update: tanggalUpdate,
                            };
                        }
                    });
                }
            });
        }

        return actualMap;
    } catch (error) {
        console.error("Error fetching actual data:", error);
        throw error;
    }
};

// Fetch data Stock PG4 dari RealisasiStokBahanPG4
export const fetchStockData = async () => {
    try {
        const stockRef = ref(database, "db_inventory/db_stok_bahan");
        const snapshot = await onValuePromise(stockRef);
        const data = snapshot.val();

        const stockMap = {};

        if (data) {
            Object.keys(data).forEach((itemCode) => {
                const itemData = data[itemCode];

                // Akses data dari node stock_PG4
                if (itemData.stock_PG4) {
                    Object.keys(itemData.stock_PG4).forEach((tanggalKey) => {
                        const node = itemData.stock_PG4[tanggalKey];

                        if (!stockMap[itemCode]) {
                            stockMap[itemCode] = {};
                        }

                        // Pastikan node memiliki nilai
                        if (node) {
                            // Gunakan tanggal_update dari node jika ada, atau gunakan tanggalKey
                            const tanggalUpdate = node.tanggal_update || tanggalKey;

                            stockMap[itemCode][tanggalUpdate] = {
                                // Stock PG4 values
                                warehouse_bpp: node.values?.warehouse_bpp || "",
                                gd_mixer_fresh_pine: node.values?.gd_mixer_fresh_pine || "",
                                gd_mat_banana: node.values?.gd_mat_banana || "",
                                gd_mat_other_fruits: node.values?.gd_mat_other_fruits || "",
                                quality_insp: node.values?.quality_insp || "",
                                transit_and_transfer: node.values?.transit_and_transfer || "",

                                // Simpan tanggal_update untuk referensi
                                tanggal_update: tanggalUpdate,
                            };
                        }
                    });
                }
            });
        }

        return stockMap;
    } catch (error) {
        console.error("Error fetching stock data:", error);
        throw error;
    }
};